import LeadCaptureBox from "@/components/LeadCaptureBox";
import EmailGateModal from "@/components/EmailGateModal";
import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { ArrowRight, ChevronRight, Copy, Check, Loader2 } from "lucide-react";
import { SITE_URL } from "@/lib/siteConfig";
import { callAIJson } from "@/lib/ai";

interface Variant { text: string; score: number; }
interface TestResult {
  open_score: number;
  spam_word_count: number;
  readability: "Easy" | "Medium" | "Hard";
  spam_words: string[];
  tone_signals: string[];
  variants: Variant[];
  tips: string[];
}

function safeArr<T>(v: unknown): T[] { return Array.isArray(v) ? (v as T[]) : []; }
function clamp(n: number, lo: number, hi: number) { return Math.max(lo, Math.min(hi, n)); }
function scoreColor(n: number) { return n >= 70 ? "text-teal" : n >= 45 ? "text-amber-500" : "text-red-500"; }
function scoreBadge(n: number) { return n >= 70 ? "bg-teal/10 border-teal/20 text-teal" : n >= 45 ? "bg-amber-50 border-amber-200 text-amber-600" : "bg-red-50 border-red-200 text-red-500"; }

function CopyBtn({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  function copy() {
    navigator.clipboard.writeText(text)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
      .catch(() => {/* silent on non-https dev */});
  }
  return (
    <button type="button" onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border border-black/[0.08] text-slate text-xs hover:bg-paper transition-colors flex-shrink-0 whitespace-nowrap">
      {copied ? <Check size={11} className="text-teal" /> : <Copy size={11} />}{copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function SubjectLineTesterPage() {
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState("");
  const resultsRef = useRef<HTMLDivElement>(null);
  const [showGate, setShowGate] = useState(false);
  const [pendingResult, setPendingResult] = useState<TestResult | null>(null);

  const chars = subject.length;
  const words = subject.trim() ? subject.trim().split(/\s+/).length : 0;
  const charStatus = chars === 0 ? null : chars < 30 ? "too short" : chars <= 60 ? "ideal" : "too long";
  const charColor = charStatus === "ideal" ? "text-teal" : charStatus ? "text-amber-500" : "text-slate";
  const truncMsg = chars > 35
    ? `⚠ Truncates at 35 chars on mobile (${chars - 35} over)`
    : chars > 60
    ? `⚠ Truncates at 60 chars in Gmail desktop (${chars - 60} over)`
    : null;

  async function runTest() {
    if (chars < 2) return;
    setError(""); setLoading(true); setResult(null);
    const prompt = `You are an elite email marketing expert. Analyze this subject line and return ONLY valid JSON.\nSubject: "${subject}"\n{"open_score":<0-100>,"spam_word_count":<integer>,"readability":"Easy"|"Medium"|"Hard","spam_words":[<max 5 risk words>],"tone_signals":[<max 6 e.g. "urgency","curiosity">],"variants":[{"text":"<curiosity variant>","score":<0-100>},{"text":"<benefit variant>","score":<0-100>},{"text":"<conversational variant>","score":<0-100>}],"tips":[<3-4 actionable tips, 1-2 sentences each>]}`;
    try {
      const p = await callAIJson<Partial<TestResult>>({ prompt, maxTokens: 1000 });
      const readOpts = ["Easy", "Medium", "Hard"] as const;
      setPendingResult({
        open_score: clamp(parseInt(String(p.open_score ?? 50)) || 50, 0, 100),
        spam_word_count: Math.max(0, parseInt(String(p.spam_word_count ?? 0)) || 0),
        readability: readOpts.includes(p.readability as typeof readOpts[number]) ? p.readability as typeof readOpts[number] : "Medium",
        spam_words: safeArr<string>(p.spam_words).slice(0, 5),
        tone_signals: safeArr<string>(p.tone_signals).slice(0, 6),
        variants: safeArr<Variant>(p.variants).slice(0, 3).map(v => ({ text: String(v?.text ?? ""), score: clamp(parseInt(String(v?.score ?? 50)) || 50, 0, 100) })),
        tips: safeArr<string>(p.tips).slice(0, 4),
      });
      setShowGate(true);
    } catch (err: unknown) {
      setError("Analysis failed: " + (err instanceof Error ? err.message : "Unknown error. Please try again."));
    } finally { setLoading(false); }
  }

  function handleGateComplete() {
    setShowGate(false);
    if (pendingResult) {
      setResult(pendingResult);
      setPendingResult(null);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
    }
  }

  return (
    <>
      {showGate && (
        <EmailGateModal toolName="subject line analysis" source="/subject-line-tester"
          onComplete={handleGateComplete} onClose={() => { setShowGate(false); handleGateComplete(); }} />
      )}
      <SeoHead title="Free Subject Line Tester — MoreMail | Score & Optimize Instantly" description="Test any email subject line for open rate potential, spam words, character length, and get 3 AI-generated A/B variants. Free, instant, no signup." url={`${SITE_URL}/subject-line-tester`} breadcrumbs={[{ name: "Home", url: SITE_URL }, { name: "Subject Line Tester", url: `${SITE_URL}/subject-line-tester` }]} />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8">
        <div className="flex items-center gap-1.5 text-xs font-medium text-slate">
          <Link to="/" className="hover:text-ink transition-colors">Home</Link>
          <ChevronRight size={12} />
          <span className="text-ink">Subject Line Tester</span>
        </div>
      </div>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 sm:pt-8 pb-6 text-center">
        <div className="inline-flex items-center px-3 py-1.5 bg-coral/10 border border-coral/20 rounded-full text-coral text-xs font-bold mb-4">Free Tool — No signup required</div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-ink leading-tight mb-3">Write subject lines that{" "}<em className="text-coral not-italic">get opened</em></h1>
        <p className="text-slate text-sm sm:text-base max-w-xl mx-auto leading-relaxed">Get an open rate score, spam word flags, live inbox preview, and 3 AI A/B variants — instantly.</p>
      </section>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-6">
        <div className="bg-white rounded-2xl border border-black/[0.08] overflow-hidden shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-4">
            <div className="w-9 h-9 rounded-xl bg-paper border border-black/[0.06] flex items-center justify-center text-base flex-shrink-0" aria-hidden="true">✉</div>
            <input
              type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !loading) runTest(); }}
              placeholder="Type or paste your subject line here..."
              maxLength={200} aria-label="Email subject line"
              className="flex-1 min-w-0 bg-transparent text-ink text-sm sm:text-base placeholder-slate/40 focus:outline-none"
            />
          </div>
          <div className="px-4 sm:px-5 py-3 border-t border-black/[0.06] bg-paper flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 sm:gap-4 text-xs font-mono flex-wrap">
              <span className={charColor}>{chars} char{chars !== 1 ? "s" : ""}{charStatus ? ` — ${charStatus}` : ""}</span>
              <span className="text-slate/50">{words} word{words !== 1 ? "s" : ""}</span>
            </div>
            <button type="button" onClick={runTest} disabled={loading || chars < 2}
              className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-coral text-white text-sm font-semibold rounded-full hover:bg-coral-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors shadow-md shadow-coral/20 whitespace-nowrap">
              {loading ? <><Loader2 size={14} className="animate-spin flex-shrink-0" />Testing...</> : <>Test this subject <ArrowRight size={14} /></>}
            </button>
          </div>
        </div>

        {error && <div role="alert" className="mt-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{error}</div>}

        {/* Live inbox preview */}
        {chars > 0 && (
          <div className="mt-4">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-2">Inbox preview</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {([{ app: "Gmail — desktop", limit: 60, from: "Alex from MoreMail" }, { app: "iPhone — lock screen", limit: 35, from: "MoreMail" }] as const).map(({ app, limit, from }) => {
                const truncated = subject.length > limit;
                return (
                  <div key={app} className="bg-white border border-black/[0.06] rounded-xl px-4 py-3">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-slate mb-1.5">{app}</p>
                    <p className="text-xs font-semibold text-ink">{from}</p>
                    <p className={`text-sm mt-0.5 truncate ${truncated ? "text-red-400" : "text-ink"}`}>{truncated ? subject.slice(0, limit) + "…" : subject}</p>
                    <p className="text-xs text-slate/50 truncate mt-0.5">Hi there, just wanted to follow up...</p>
                  </div>
                );
              })}
            </div>
            {truncMsg && <p className="text-xs text-amber-600 mt-2 font-medium">{truncMsg}</p>}
          </div>
        )}
      </section>

      {result && (
        <section ref={resultsRef} className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          {/* Metric cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
            {[
              { label: "Open score", val: result.open_score, sub: "/ 100", color: scoreColor(result.open_score) },
              { label: "Length", val: chars, sub: charStatus ?? "chars", color: chars >= 30 && chars <= 60 ? "text-teal" : "text-amber-500" },
              { label: "Spam words", val: result.spam_word_count, sub: "detected", color: result.spam_word_count === 0 ? "text-teal" : result.spam_word_count <= 2 ? "text-amber-500" : "text-red-500" },
              { label: "Readability", val: result.readability, sub: "grade", color: result.readability === "Easy" ? "text-teal" : result.readability === "Medium" ? "text-amber-500" : "text-red-500" },
            ].map(({ label, val, sub, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-3 sm:py-4 px-3 sm:px-4 text-center">
                <p className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wide text-slate mb-1">{label}</p>
                <p className={`font-display text-xl sm:text-2xl font-bold leading-none ${color}`}>{val}</p>
                <p className="text-[10px] sm:text-xs text-slate mt-1 truncate">{sub}</p>
              </div>
            ))}
          </div>

          {/* Tags */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />Spam & risk words</p>
              {result.spam_words.length === 0 ? <p className="text-xs text-slate italic">None detected — good sign.</p> : <div className="flex flex-wrap gap-2">{result.spam_words.map((w, i) => <span key={i} className="px-2.5 py-1 rounded-full bg-red-50 border border-red-200 text-red-500 text-xs font-semibold">{w}</span>)}</div>}
            </div>
            <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5">
              <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-coral flex-shrink-0" />Tone signals</p>
              {result.tone_signals.length === 0 ? <p className="text-xs text-slate italic">No tone signals detected.</p> : <div className="flex flex-wrap gap-2">{result.tone_signals.map((s, i) => <span key={i} className="px-2.5 py-1 rounded-full bg-coral/10 border border-coral/20 text-coral text-xs font-semibold">{s}</span>)}</div>}
            </div>
          </div>

          {/* Variants */}
          <div className="bg-white rounded-2xl border border-black/[0.06] overflow-hidden mb-4">
            <div className="px-4 sm:px-5 py-3.5 border-b border-black/[0.06] bg-paper">
              <p className="text-xs font-bold uppercase tracking-widest text-slate">A/B variants to test</p>
            </div>
            <div className="divide-y divide-black/[0.04]">
              {result.variants.length === 0 ? (
                <p className="px-5 py-4 text-sm text-slate italic">No variants generated.</p>
              ) : result.variants.map((v, i) => (
                <div key={i} className="px-4 sm:px-5 py-4 flex items-center gap-3 min-w-0">
                  <span className="flex-1 min-w-0 text-sm text-ink">{v.text}</span>
                  <span className={`text-xs font-bold px-2 sm:px-2.5 py-1 rounded-full border flex-shrink-0 ${scoreBadge(v.score)}`}>{v.score}/100</span>
                  <CopyBtn text={v.text} />
                </div>
              ))}
            </div>
          </div>

          {/* Tips */}
          <div className="bg-white rounded-2xl border border-black/[0.06] p-4 sm:p-5 mb-5">
            <p className="text-xs font-bold uppercase tracking-widest text-slate mb-3 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-teal flex-shrink-0" />Tips to improve</p>
            {result.tips.length === 0 ? <p className="text-xs text-slate italic">No tips generated.</p> : <div className="space-y-2">{result.tips.map((t, i) => (<div key={i} className="flex gap-3 bg-paper rounded-xl px-3 sm:px-4 py-3 text-sm"><span className="font-bold text-coral flex-shrink-0">{i + 1}.</span><span className="text-slate leading-relaxed">{t}</span></div>))}</div>}
          </div>

          <div className="bg-ink rounded-2xl px-5 sm:px-6 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="min-w-0">
              <h3 className="font-display text-lg sm:text-xl font-bold text-white mb-1">Test unlimited subject lines with MoreMail</h3>
              <p className="text-sm text-white/50">AI campaigns, warmup, and deliverability — all in one place.</p>
            </div>
            <LeadCaptureBox />
          </div>
        </section>
      )}

      {!result && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12 sm:pb-16">
          <div className="grid grid-cols-3 gap-3 text-center">
            {[{ stat: "26%", label: "avg open rate lift" }, { stat: "3×", label: "variants per test" }, { stat: "Free", label: "no signup needed" }].map(({ stat, label }) => (
              <div key={label} className="bg-white rounded-2xl border border-black/[0.06] py-4 sm:py-5">
                <div className="font-display text-xl sm:text-2xl font-bold text-coral">{stat}</div>
                <div className="text-xs text-slate mt-1">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
